import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { publicTicketSalesAPI } from '../../../../services/membershipApi';
import { normalizeSriLankanNic, isValidSriLankanNic, parseNIC } from '../../../../utils/nicUtils';
import TicketCard from '../TicketCard';
import { Icon } from '../../Common/Icons';
import { PublicShell, EventHero, PublicMessage, Section, Field, inputCls, normalizePhone, fmtMoney } from './publicTicketShared';

const EMAIL_RX = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;
const METHODS = [
  { key: 'online', flag: 'online', icon: 'credit-card', title: 'Pay online', text: 'Visa / Mastercard through our secure payment gateway. Your ticket arrives instantly.' },
  { key: 'bank_transfer', flag: 'bankTransfer', icon: 'building', title: 'Bank transfer', text: 'Transfer to SLIM and upload the slip. Your ticket is emailed once it is confirmed.' },
  { key: 'cashier', flag: 'cashier', icon: 'cash', title: 'Pay at the cashier', text: 'Pay by cash or card at the SLIM office. Your ticket is emailed once paid.' },
];

/** Phone input that cleans the number (digits only, 10 digits) when the field is left. */
const PhoneInput = ({ value, onChange, error, ...props }) => (
  <input
    {...props}
    type="tel"
    inputMode="numeric"
    autoComplete="tel"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onBlur={() => { const n = normalizePhone(value); if (n) onChange(n); }}
    placeholder="07X XXX XXXX"
    className={`${inputCls} ${error ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/15' : ''}`}
  />
);

/**
 * Public ticket sales page (/membership/tickets/:token): who you are (NIC + email; members are
 * recognised and filled in), the ticket, and how to pay. Creates the order, then opens the
 * buyer's order page where they pay and get the QR ticket.
 */
const PublicTicketSale = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [nic, setNic] = useState('');
  const [email, setEmail] = useState('');
  const [who, setWho] = useState(null); // lookup result once NIC + email are checked
  const [checking, setChecking] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [form, setForm] = useState({ nameWithInitials: '', dob: '', gender: '', address: '', mobile: '', whatsapp: '', whatsappSameAsMobile: true });
  const [ticketChosen, setTicketChosen] = useState(true);
  const [method, setMethod] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    publicTicketSalesAPI.getPage(token)
      .then((r) => {
        const d = r.data?.data;
        setPage(d);
        const first = METHODS.find((m) => d?.payment?.[m.flag]);
        if (first) setMethod(first.key);
      })
      .catch((err) => setLoadError(err.response?.data?.message || 'This ticket link could not be opened. Please check your connection and try again.'));
  }, [token]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const nicParsed = useMemo(() => (isValidSriLankanNic(nic) ? parseNIC(nic) : null), [nic]);

  const checkDetails = async (e) => {
    e?.preventDefault();
    setLookupError('');
    const n = normalizeSriLankanNic(nic);
    if (!isValidSriLankanNic(n)) return setLookupError('Enter a valid NIC number, e.g. 199012345678 or 901234567V.');
    if (!EMAIL_RX.test(email.trim())) return setLookupError('Enter a valid email address.');
    setChecking(true);
    try {
      const r = await publicTicketSalesAPI.lookup(token, { nic: n, email: email.trim() });
      const d = r.data?.data;
      setWho(d);
      setNic(n);
      if (d.isMember) {
        setForm((f) => ({ ...f, mobile: d.member.mobile || f.mobile }));
      } else {
        const info = d.nicInfo || parseNIC(n);
        setForm((f) => ({ ...f, dob: info?.dob || f.dob, gender: info?.gender || f.gender }));
      }
    } catch (err) {
      setLookupError(err.response?.data?.message || 'Could not check your details. Please try again.');
    } finally { setChecking(false); }
  };
  const editIdentity = () => { setWho(null); setSubmitError(''); };

  const validate = () => {
    const e = {};
    if (!who.isMember) {
      if (form.nameWithInitials.trim().length < 3) e.nameWithInitials = 'Enter your name with initials';
      if (!form.dob) e.dob = 'Enter your date of birth';
      if (!form.gender) e.gender = 'Select your gender';
      if (form.address.trim().length < 5) e.address = 'Enter your address';
    }
    if (!normalizePhone(form.mobile)) e.mobile = 'Enter a 10-digit mobile number, e.g. 0771234567';
    if (!form.whatsappSameAsMobile && !normalizePhone(form.whatsapp)) e.whatsapp = 'Enter a 10-digit WhatsApp number';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const paid = (page?.ticket?.price || 0) > 0;
  const submit = async () => {
    setSubmitError('');
    if (!validate()) return;
    if (!ticketChosen) return setSubmitError('Choose your ticket.');
    if (paid && !method) return setSubmitError('Choose how you would like to pay.');
    setSubmitting(true);
    try {
      const r = await publicTicketSalesAPI.purchase(token, {
        nic, email: email.trim(),
        nameWithInitials: form.nameWithInitials, dob: form.dob, gender: form.gender, address: form.address,
        mobile: normalizePhone(form.mobile), whatsapp: normalizePhone(form.whatsapp), whatsappSameAsMobile: form.whatsappSameAsMobile,
        paymentMethod: paid ? method : undefined,
      });
      const { orderToken, resumed } = r.data?.data || {};
      navigate(`/membership/tickets/${token}/order/${orderToken}`, { state: { autoPay: !resumed && paid && method === 'online', fresh: true } });
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Could not complete your order. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (loadError) return <PublicShell><PublicMessage icon="x-circle" tone="rose" title="Link not available">{loadError}</PublicMessage></PublicShell>;
  if (!page) {
    return (
      <PublicShell>
        <div className="animate-pulse space-y-6">
          <div className="h-64 rounded-3xl bg-gray-200" />
          <div className="grid gap-6 lg:grid-cols-5"><div className="h-80 rounded-2xl bg-gray-100 lg:col-span-3" /><div className="h-80 rounded-2xl bg-gray-100 lg:col-span-2" /></div>
        </div>
      </PublicShell>
    );
  }

  const { event, template, ticket } = page;
  const holderName = who?.isMember ? who.member.name : (form.nameWithInitials || 'Your name');
  // event title / venue / time already resolved by the server (event details > template > event)
  const previewData = {
    eventTitle: event.title, date: event.startDate, venue: event.venue, timeText: event.timeText,
    holderName, allocationName: ticket.name, price: ticket.price, currency: ticket.currency, ticketNumber: 'TKT ······',
  };

  return (
    <PublicShell>
      <div className="space-y-6 sm:space-y-8">
        <EventHero event={event} template={template} />

        {!page.open ? (
          <PublicMessage icon="clock" tone="amber" title="Ticket sales are closed">{page.closedReason} For help, please contact SLIM.</PublicMessage>
        ) : ticket.soldOut ? (
          <PublicMessage icon="tag" tone="amber" title="Sold out">All tickets for this event have been sold. Please contact SLIM if you would like to be told about any returns.</PublicMessage>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:items-start">
            <div className="space-y-5 lg:col-span-3">
              {event.description && (
                <p className="whitespace-pre-line rounded-2xl bg-white p-5 text-sm leading-relaxed text-gray-600 shadow-sm ring-1 ring-gray-100">{event.description}</p>
              )}

              {/* 1. Who is buying */}
              <Section step={1} title="Your details" done={!!who}
                aside={who && <button type="button" onClick={editIdentity} className="text-sm font-medium text-violet-700 hover:underline">Change</button>}>
                {!who ? (
                  <form onSubmit={checkDetails} className="space-y-4">
                    <p className="text-sm text-gray-500">Start with your NIC and email. SLIM members are recognised automatically.</p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="NIC number" required hint={nicParsed ? `Born ${new Date(nicParsed.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ${nicParsed.gender}` : 'Old (901234567V) or new (199012345678)'}>
                        <input value={nic} onChange={(e) => setNic(e.target.value.toUpperCase())} className={inputCls} placeholder="199012345678" autoComplete="off" maxLength={14} required />
                      </Field>
                      <Field label="Email address" required hint="Your ticket is sent here">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@example.com" autoComplete="email" required />
                      </Field>
                    </div>
                    {lookupError && <p className="rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{lookupError}</p>}
                    <button type="submit" disabled={checking} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-600/25 transition hover:bg-violet-700 disabled:opacity-60 sm:w-auto">
                      {checking ? 'Checking' : 'Continue'}<Icon name="arrow-right" className="h-4 w-4" />
                    </button>
                  </form>
                ) : who.existingOrder ? (
                  <div className="rounded-xl bg-violet-50 p-4">
                    <p className="font-medium text-violet-900">You already have an order for this event.</p>
                    <p className="mt-1 text-sm text-violet-800">Open it to see your ticket{who.existingOrder.status === 'pending_payment' ? ' or finish paying' : ''}.</p>
                    <button type="button" onClick={() => navigate(`/membership/tickets/${token}/order/${who.existingOrder.orderToken}`)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">
                      View my order<Icon name="arrow-right" className="h-4 w-4" />
                    </button>
                  </div>
                ) : who.nicTaken ? (
                  <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">A ticket for this event has already been bought with this NIC. Use the email address you bought it with, or contact SLIM.</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 text-sm">
                      <span className="font-mono font-medium text-gray-800">{nic}</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-700">{email}</span>
                    </div>

                    {who.isMember ? (
                      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"><Icon name="award" className="h-5 w-5" /></span>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">SLIM member</p>
                          <p className="truncate text-base font-semibold text-gray-900">{who.member.fullName || who.member.name}</p>
                          <p className="mt-0.5 inline-flex rounded-md bg-white px-2 py-0.5 font-mono text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">Membership ID: {who.member.membershipId}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Name with initials" required error={errors.nameWithInitials} className="sm:col-span-2">
                          <input value={form.nameWithInitials} onChange={(e) => set('nameWithInitials', e.target.value)} className={inputCls} placeholder="e.g. K. A. Silva" autoComplete="name" />
                        </Field>
                        <Field label="Date of birth" required error={errors.dob} hint={nicParsed ? 'Filled from your NIC' : undefined}>
                          <input type="date" value={form.dob} onChange={(e) => set('dob', e.target.value)} disabled={!!nicParsed} className={inputCls} max={new Date().toISOString().slice(0, 10)} />
                        </Field>
                        <Field label="Gender" required error={errors.gender} hint={nicParsed ? 'Filled from your NIC' : undefined}>
                          <div className="grid grid-cols-2 gap-2">
                            {['Male', 'Female'].map((g) => (
                              <button key={g} type="button" disabled={!!nicParsed} onClick={() => set('gender', g)} aria-pressed={form.gender === g}
                                className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition disabled:cursor-not-allowed ${form.gender === g ? 'border-violet-600 bg-violet-50 text-violet-800 ring-2 ring-violet-600/20' : 'border-gray-200 bg-white text-gray-600 hover:border-violet-300'} ${nicParsed && form.gender !== g ? 'opacity-40' : ''}`}>
                                {g}
                              </button>
                            ))}
                          </div>
                        </Field>
                        <Field label="Address" required error={errors.address} className="sm:col-span-2">
                          <textarea value={form.address} onChange={(e) => set('address', e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="House no., street, city" autoComplete="street-address" />
                        </Field>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Mobile number" required error={errors.mobile} hint="10 digits; spaces and symbols are removed">
                        <PhoneInput value={form.mobile} onChange={(v) => set('mobile', v)} error={errors.mobile} />
                      </Field>
                      <div>
                        <Field label="WhatsApp number" required={!form.whatsappSameAsMobile} error={errors.whatsapp}>
                          {form.whatsappSameAsMobile
                            ? <input value={normalizePhone(form.mobile) || form.mobile} disabled className={inputCls} aria-label="WhatsApp number" />
                            : <PhoneInput value={form.whatsapp} onChange={(v) => set('whatsapp', v)} error={errors.whatsapp} />}
                        </Field>
                        <label className="mt-1.5 flex w-fit cursor-pointer items-center gap-2 text-sm text-gray-600">
                          <input type="checkbox" checked={form.whatsappSameAsMobile} onChange={(e) => set('whatsappSameAsMobile', e.target.checked)} className="h-4 w-4 rounded text-violet-600 focus:ring-violet-500" />
                          Same as mobile number
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </Section>

              {who && !who.existingOrder && !who.nicTaken && (
                <>
                  {/* 2. Ticket */}
                  <Section step={2} title="Your ticket" done={ticketChosen}>
                    <button type="button" onClick={() => setTicketChosen((v) => !v)} aria-pressed={ticketChosen}
                      className={`flex w-full items-center gap-4 rounded-2xl border-2 p-4 text-left transition ${ticketChosen ? 'border-violet-600 bg-violet-50/60 shadow-md shadow-violet-600/10' : 'border-gray-200 hover:border-violet-300'}`}>
                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${ticketChosen ? 'border-violet-600 bg-violet-600 text-white' : 'border-gray-300'}`}>
                        {ticketChosen && <Icon name="check" className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-gray-900">{ticket.name}</span>
                        <span className="block text-xs text-gray-500">1 person · {ticket.remaining <= 10 ? <span className="font-medium text-amber-600">Only {ticket.remaining} left</span> : 'Admits one'}</span>
                      </span>
                      <span className="text-lg font-bold text-gray-900">{fmtMoney(ticket.price, ticket.currency)}</span>
                    </button>
                    <p className="mt-2 text-xs text-gray-500">One ticket per person. Each ticket has its own QR code with the holder's details.</p>
                  </Section>

                  {/* 3. Payment */}
                  {paid && (
                    <Section step={3} title="How would you like to pay?" done={!!method}>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {METHODS.filter((m) => page.payment[m.flag]).map((m) => (
                          <button key={m.key} type="button" onClick={() => setMethod(m.key)} aria-pressed={method === m.key}
                            className={`flex flex-col gap-2 rounded-2xl border-2 p-4 text-left transition ${method === m.key ? 'border-violet-600 bg-violet-50/60 shadow-md shadow-violet-600/10' : 'border-gray-200 hover:border-violet-300'}`}>
                            <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${method === m.key ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600'}`}><Icon name={m.icon} className="h-5 w-5" /></span>
                            <span className="font-semibold text-gray-900">{m.title}</span>
                            <span className="text-xs leading-relaxed text-gray-500">{m.text}</span>
                          </button>
                        ))}
                      </div>
                    </Section>
                  )}

                  {submitError && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">{submitError}</p>}
                  <button type="button" onClick={submit} disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-violet-600 px-6 py-4 text-base font-semibold text-white shadow-xl shadow-violet-600/25 transition hover:bg-violet-700 disabled:opacity-60">
                    {submitting ? 'Placing your order' : !paid ? 'Get my free ticket' : method === 'online' ? `Pay ${fmtMoney(ticket.price, ticket.currency)} securely` : 'Place order'}
                    {!submitting && <Icon name="arrow-right" className="h-5 w-5" />}
                  </button>
                  <p className="text-center text-xs text-gray-400">By continuing you confirm these details are correct. They are printed on your ticket and checked at the entrance.</p>
                </>
              )}
            </div>

            {/* Live ticket + summary */}
            <aside className="space-y-4 lg:sticky lg:top-6 lg:col-span-2">
              <div className="rounded-2xl bg-white p-3 shadow-sm ring-1 ring-gray-100 sm:p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-700">Your ticket</p>
                <TicketCard vertical template={template} data={previewData} qrLabel={paid ? 'Your QR code appears once paid' : 'Your QR code appears here'} />
              </div>
              <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100">
                <p className="text-sm font-semibold text-gray-900">Order summary</p>
                <div className="mt-3 flex justify-between text-sm text-gray-600"><span>{ticket.name} × 1</span><span>{fmtMoney(ticket.price, ticket.currency)}</span></div>
                <div className="mt-3 flex justify-between border-t border-gray-100 pt-3 text-base font-bold text-gray-900"><span>Total</span><span>{fmtMoney(ticket.price, ticket.currency)}</span></div>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-gray-500"><Icon name="lock" className="h-3.5 w-3.5" />Card details are entered only on the bank's secure page.</p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </PublicShell>
  );
};

export default PublicTicketSale;
